import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useChargeStageData } from '../../../hooks/useChargeStageData'
import { getFormattedTooltipHeader, getSeriesTooltipHtml } from '../../../utils/tooltipFormatters'
import { patchApexChartsPassiveTouch } from '../../../utils/fixApexChartsEvents'
import { formatNumberBySignificantFigures } from '../../../utils/chartUtils'

function ChartDisplay({ 
  series, 
  annotations, 
  onXAxisRangeUpdate, 
  chargeStageDevice = 'bat1', 
  chargeStageMeasurement = 'chargeStage', 
}) {
  useEffect(() => { patchApexChartsPassiveTouch() }, [])

  const [currentXAxisRange, setCurrentXAxisRange] = useState({ min: null, max: null })

  const { 
    chargeStageTooltipData, 
    CHARGE_STAGE_NAME_MAPPING, 
    isLoadingChargeStage, 
    chargeStageError, 
  } = useChargeStageData(chargeStageDevice, chargeStageMeasurement, currentXAxisRange)

  const parseToTimestampIfPossible = useCallback((val) => {
    if (typeof val === 'number') return val
    if ((typeof val === 'string' && val.length > 0) || val instanceof Date) {
      const ts = new Date(val).getTime()
      return isNaN(ts) ? undefined : ts
    }
    return undefined
  }, [])

  useEffect(() => {
    const chartIsReady = series?.some(s => s.data?.length > 0)

    if (!chartIsReady) {
      setCurrentXAxisRange({ min: null, max: null })
      return
    }

    let minTime = Infinity
    let maxTime = -Infinity

    series.forEach(s => {
      s.data?.forEach(dataPoint => {
        if (!dataPoint) return

        let xValue
        if (Array.isArray(dataPoint)) {
          [xValue] = dataPoint
        } else if (typeof dataPoint === 'object' && dataPoint.x !== undefined) {
          xValue = dataPoint.x
        } else {
          return
        }
        
        const timestamp = parseToTimestampIfPossible(xValue)

        if (timestamp !== undefined) {
          minTime = Math.min(minTime, timestamp)
          maxTime = Math.max(maxTime, timestamp)
        }
      })
    })

    if (minTime !== Infinity && maxTime !== -Infinity) {
      setCurrentXAxisRange({ min: minTime, max: maxTime })
    } else {
      setCurrentXAxisRange({ min: null, max: null })
    }
  }, [series, parseToTimestampIfPossible])

  const processedAnnotations = useMemo(() => {
    if (!annotations) return undefined
    return {
      points: annotations.points?.map(point => ({
        ...point,
        marker: {
          ...point.marker,
          size: 8, 
          shape: 'circle',
        },
        label: {
          ...point.label,
          style: {
            ...point.label?.style,
            fontSize: '14px',
          },
        },
      })),
      xaxis: annotations.xaxis?.map(line => ({
        ...line,
        label: {
          ...line.label,
          style: {
            ...line.label?.style,
            fontSize: '14px',
          },
        },
      })),
    }
  }, [annotations])

  const chartOptions = useMemo(() => {
    const originalSeriesMap = new Map((series || []).map(s => [s.name, s]))

    return {
      chart: {
        type: 'line',
        zoom: { enabled: true },
        animations: { enabled: false },
        toolbar: {
          show: false,
        },
        events: { 
          zoomed(_chartContext, { xaxis }) {
            if (xaxis && typeof xaxis.min === 'number' && typeof xaxis.max === 'number') {
              setCurrentXAxisRange({ min: xaxis.min, max: xaxis.max })
              if (onXAxisRangeUpdate) {
                onXAxisRangeUpdate(xaxis.min, xaxis.max)
              }
            }
          },
          beforeResetZoom(_chartContext, opts) {
            return opts
          },
        },
        background: 'transparent',
        redrawOnParentResize: false,
        redrawOnWindowResize: true,
        fontFamily: 'inherit',
      },
      tooltip: {
        enabled: true,
        shared: true,
        followCursor: true,
        intersect: false,
        custom({ seriesIndex, dataPointIndex, w }) {
          let xTimestamp
          const { globals } = w
          const capturedDPI = globals.capturedDataPointIndex

          // Attempt 1: From tooltipSharedXvalues (most reliable for shared tooltips)
          if (globals.tooltipSharedXvalues?.length > 0 && capturedDPI !== undefined && 
              capturedDPI >= 0 && capturedDPI < globals.tooltipSharedXvalues.length) {
            xTimestamp = parseToTimestampIfPossible(globals.tooltipSharedXvalues[capturedDPI])
          }

          // Attempt 2: From series data (w.globals.series)
          if (xTimestamp === undefined) {
            const currentSeries = globals.series?.[seriesIndex]
            if (currentSeries?.data && Array.isArray(currentSeries.data)) {
              const pointData = currentSeries.data[dataPointIndex]
              if (pointData != null && dataPointIndex >= 0 && dataPointIndex < currentSeries.data.length) {
                let rawXValue
                if (Array.isArray(pointData) && pointData.length > 0) [rawXValue] = pointData
                else if (typeof pointData === 'object' && pointData.x !== undefined) rawXValue = pointData.x
                
                if (rawXValue !== undefined) {
                  xTimestamp = parseToTimestampIfPossible(rawXValue)
                }
              }
            }
          }

          // Attempt 3: From seriesX
          if (xTimestamp === undefined) {
            const {seriesX} = globals
            const relevantDPI = globals.tooltip.shared && capturedDPI !== undefined ? capturedDPI : dataPointIndex

            if (seriesX?.[seriesIndex] && Array.isArray(seriesX[seriesIndex])) {
              const seriesXArray = seriesX[seriesIndex]
              if (relevantDPI >= 0 && relevantDPI < seriesXArray.length) {
                const pointDataFromSeriesX = seriesXArray[relevantDPI]
                let rawXValue
                if (Array.isArray(pointDataFromSeriesX) && pointDataFromSeriesX.length > 0) {
                    [rawXValue] = pointDataFromSeriesX
                } else if (typeof pointDataFromSeriesX === 'object' && pointDataFromSeriesX !== null && pointDataFromSeriesX.x !== undefined) {
                    rawXValue = pointDataFromSeriesX.x
                } else if (typeof pointDataFromSeriesX === 'number' || typeof pointDataFromSeriesX === 'string') {
                    rawXValue = pointDataFromSeriesX
                }
                
                if (rawXValue !== undefined) {
                  xTimestamp = parseToTimestampIfPossible(rawXValue)
                }
              }
            }
          }
          
          const headerHtml = getFormattedTooltipHeader(xTimestamp, chargeStageTooltipData, isLoadingChargeStage, chargeStageError, CHARGE_STAGE_NAME_MAPPING)
          const seriesHtml = w.config.series.map((sConfig, i) => 
            getSeriesTooltipHtml(sConfig, i, w, xTimestamp, originalSeriesMap),
          ).join('')

          return headerHtml + seriesHtml
        },
      },
      dataLabels: { enabled: false },
      stroke: { width: 2, curve: 'straight', lineCap: 'round' },
      grid: {
        padding: { right: 30, left: 20 },
        strokeDashArray: 0,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
          formatter: val => {
            const date = new Date(val)
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
          },
          rotate: 0,
          tickAmount: 6,
        },
        crosshairs: {
          show: true,
          stroke: { color: '#b6b6b6', width: 1, dashArray: 0 },
        },
      },
      yaxis: {
        title: { text: 'Value' },
        labels: { formatter: v => typeof v === 'number' ? formatNumberBySignificantFigures(v, 3) : '', minWidth: 40 },
        forceNiceScale: true,
        decimalsInFloat: 2,
        tickAmount: 5,
      },
      annotations: processedAnnotations,
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        onItemClick: { toggleDataSeries: true },
        onItemHover: { highlightDataSeries: true },
      },
      markers: {
        size: 0,
        hover: { size: 3, sizeOffset: 3 },
      },
      plotOptions: { line: { pointIntervalUnit: undefined } },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 400 },
            legend: { position: 'bottom' },
          },
        },
      ],
    }
  }, [
    series, 
    processedAnnotations, 
    onXAxisRangeUpdate, 
    chargeStageTooltipData, 
    CHARGE_STAGE_NAME_MAPPING, 
    isLoadingChargeStage, 
    chargeStageError,
    parseToTimestampIfPossible, // Added as it's used in custom tooltip
  ])

  if (series?.some(s => s.data?.length > 0)) {
    return (
      <div className="chart-container mt-4">
        <div className="chart-interaction-hints mb-2 text-muted small">
          <div className="d-flex align-items-center justify-content-start">
            <i className="bi bi-info-circle me-2"></i>
            <span className="me-3">Hover over chart to see detailed values</span>
            <i className="bi bi-zoom-in me-2"></i>
            <span>Click and drag to zoom into specific time periods</span>
          </div>
        </div>
        <ReactApexChart options={chartOptions} series={series} type="line" height={500} />
      </div>
    )
  }

  return (
    <div className="alert alert-info">
      <i className="bi bi-info-circle me-2"></i>
      No data available for the selected combination of devices and measurements.
      Please select different options or try a different time range.
    </div>
  )
}

export default ChartDisplay